import { getText } from '../translations/translations.js';
import { initializeExploreSearch } from './header-explore.js';
import { initializeDiscoverBar } from './header-discover.js';
import { HEADER_EXPLORE_DEFAULT, HEADER_DISCOVER_DEFAULT, HEADER_BUTTONS, LOGGING } from '/settings.js';
import { clearAllUI, restoreActiveFlowFromUrl } from '../utils/clearAll.js';
import { parseUrlForExploreAreaPackTag, parseUrlForDiscover } from '../utils/url-utils.js';
import { createIconElement } from '../utils/icon.js';
// import { createAreaSwitcherButton } from './header-area-switcher.js'; // Removed: Handled by index.js

// Exported function to set the correct header button as active
export function setHeaderButtonActive(type) {
    const btnExplore = document.querySelector('.header-action-btn.explore');
    const btnDiscover = document.querySelector('.header-action-btn.discover');
    if (btnExplore) btnExplore.classList.remove('active');
    if (btnDiscover) btnDiscover.classList.remove('active');
    if (type === 'explore' && btnExplore) btnExplore.classList.add('active');
    if (type === 'discover' && btnDiscover) btnDiscover.classList.add('active');
}

function clearHeaderButtonsActive() {
    // Ensure both buttons are cleared; used by popup close and direct URL flows
    const btnExplore = document.querySelector('.header-action-btn.explore');
    const btnDiscover = document.querySelector('.header-action-btn.discover');
    if (btnExplore) btnExplore.classList.remove('active');
    if (btnDiscover) btnDiscover.classList.remove('active');
}

// Accept container and options
export async function initHeaderButtons(container, options = {}) {
    const { autoOpen = true, activeFlow = null } = options;

    // NOTE: We do NOT clear the container here anymore, index.js does it.
    // If container is not provided, we try to fallback (legacy support)
    if (!container) {
        container = document.getElementById('search-bar-container');
        if (container) container.innerHTML = ''; // Only clear if we are in legacy mode
    }
    if (!container) return;

    // Get translations
    const exploreText = await getText('buttons.explore') || 'EXPLORE';
    const mapsText = await getText('buttons.maps') || 'MAPS';

    // Create wrapper for all buttons (menu toggle + explore + maps)
    const wrapper = document.createElement('div');
    wrapper.className = 'header-toggle-btns';

    // --- 0. Move Menu Toggle Button into wrapper ---
    // CRITICAL: Menu toggle should ALWAYS exist - it's part of the general app UI
    const menuToggle = document.getElementById('menu-toggle');
    if (!menuToggle) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('[HeaderButtons] CRITICAL: Menu toggle button not found! It should always exist in the HTML.');
        // Continue creating other buttons even if menu toggle is missing (shouldn't happen)
    } else {
        // Remove from wherever it currently is (header-left or previous wrapper)
        const headerLeft = document.querySelector('.header-left');
        if (headerLeft && headerLeft.contains(menuToggle)) {
            try {
                headerLeft.removeChild(menuToggle);
            } catch (error) {
                if (LOGGING.ENABLED && LOGGING.MENU) {
                    console.warn('[HeaderButtons] Could not remove menu toggle from header-left:', error);
                }
            }
        }
        // Also check if it's already in a wrapper (from previous initialization)
        const existingWrapper = menuToggle.closest('.header-toggle-btns');
        if (existingWrapper && existingWrapper.contains(menuToggle)) {
            try {
                existingWrapper.removeChild(menuToggle);
            } catch (error) {
                if (LOGGING.ENABLED && LOGGING.MENU) {
                    console.warn('[HeaderButtons] Could not remove menu toggle from wrapper:', error);
                }
            }
        }
        // Add to wrapper as first element (menu toggle is always first)
        wrapper.appendChild(menuToggle);
    }

    // --- Area Switcher Logic Removed (Moved to index.js) ---

    // --- 1. Explore Button ---
    if (HEADER_BUTTONS.SHOW_EXPLORE !== false) { // Default to true if undefined
        const btnExplore = document.createElement('button');
        btnExplore.className = 'header-action-btn explore';
        btnExplore.onclick = () => setActive('explore');
        const exploreImg = createIconElement('compass-solid');
        const exploreTextSpan = document.createElement('span');
        exploreTextSpan.className = 'button-text';
        exploreTextSpan.textContent = exploreText;
        btnExplore.appendChild(exploreImg);
        btnExplore.appendChild(exploreTextSpan);
        wrapper.appendChild(btnExplore);
    }

    // --- 2. Discover Button (now "MAPS") ---
    if (HEADER_BUTTONS.SHOW_DISCOVER !== false) { // Default to true if undefined
        const btnDiscover = document.createElement('button');
        btnDiscover.className = 'header-action-btn discover';
        btnDiscover.onclick = () => setActive('discover');
        const discoverImg = createIconElement('map-location-dot-solid-full');
        const discoverTextSpan = document.createElement('span');
        discoverTextSpan.className = 'button-text';
        discoverTextSpan.textContent = mapsText;
        btnDiscover.appendChild(discoverImg);
        btnDiscover.appendChild(discoverTextSpan);
        wrapper.appendChild(btnDiscover);
    }

    container.appendChild(wrapper);

    // Set initial active state based on settings, but only if autoOpen is true
    if (activeFlow) {
        setHeaderButtonActive(activeFlow);
    } else if (autoOpen) {
        if (HEADER_DISCOVER_DEFAULT && HEADER_BUTTONS.SHOW_DISCOVER !== false) {
            setActive('discover');
        } else if (HEADER_EXPLORE_DEFAULT && HEADER_BUTTONS.SHOW_EXPLORE !== false) {
            setActive('explore');
        }
    }

    function setActive(type) {
        setHeaderButtonActive(type);
        if (type === 'explore') {
            initializeExploreSearch();
        } else if (type === 'discover') {
            initializeDiscoverBar();
        }
        if (LOGGING.ENABLED && LOGGING.MENU) console.log(type + ' selected');
    }

    // Listen for Explore popup close event - clear active state
    window.addEventListener('explorePopupClosed', () => {
        setTimeout(() => {
            clearHeaderButtonsActive();
        }, 100);
    });

    // Listen for Discover/Maps popup close event - clear active state
    window.addEventListener('discoverPopupClosed', () => {
        setTimeout(() => {
            clearHeaderButtonsActive();
        }, 100);
    });

    // When a map is selected, immediately clear header button active state
    window.addEventListener('discoverMapSelected', () => {
        clearHeaderButtonsActive();
    });

    // When explore search executes, clear active state
    window.addEventListener('exploreActionExecuted', () => {
        clearHeaderButtonsActive();
    });

    // On initial load, reflect URL state (e.g., direct /area/pack/tags or /area/mapSlug)
    restoreActiveFlowFromUrl();
}